import Navbar from '../components/Navbar';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SECTIONS = [
  {
    title: 'What Data We Collect',
    content: [
      'When you log in via Google, we receive your name, email address, and profile picture from Google\'s OAuth service. We store this to identify your account.',
      'We record which DLC packs you have purchased so we can unlock the corresponding calculator buttons on your account.',
      'Stripe processes all payment information. We never see, store, or handle your card number, expiry date, or CVV. For details on how Stripe handles your data, see stripe.com/privacy.',
      'We store session tokens (as secure HTTP-only cookies) to keep you logged in for up to 7 days.',
      'We collect aggregate statistics about total operations performed and total revenue — no personally identifiable information is included in these numbers.',
    ],
  },
  {
    title: 'How We Use Your Data',
    content: [
      'To authenticate you and remember your session.',
      'To record and fulfil your purchases (i.e. unlock the buttons you paid for).',
      'To display your name and avatar in the navigation bar.',
      'To calculate aggregate statistics shown on the homepage (e.g. "Total Player Spend"). These are anonymised.',
      'We do not sell your data. We do not use your data for advertising. We are a satirical calculator, not an ad network.',
    ],
  },
  {
    title: 'Third-Party Services',
    content: [
      'Google OAuth — used for login. Google\'s privacy policy applies: policies.google.com/privacy',
      'Stripe — used for payment processing. Stripe\'s privacy policy applies: stripe.com/privacy',
      'We do not use any advertising trackers, analytics pixels, or third-party marketing tools.',
    ],
  },
  {
    title: 'Cookies',
    content: [
      'We use a single session cookie ("session_token") to keep you logged in. This cookie is HTTP-only and Secure — it cannot be read by JavaScript and is only sent over HTTPS.',
      'We do not use advertising cookies, tracking cookies, or third-party analytics cookies.',
      'You can delete the session cookie at any time by logging out or clearing your browser cookies. This will log you out of your account.',
    ],
  },
  {
    title: 'Data Retention',
    content: [
      'Your account data (name, email, purchased packs) is retained for as long as your account exists.',
      'To request deletion of your account and all associated data, email us at the contact address below. We will process the request within 30 days.',
      'Session tokens expire after 7 days of inactivity.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'You have the right to access the data we hold about you.',
      'You have the right to request correction or deletion of your data.',
      'You have the right to withdraw consent (by deleting your account).',
      'If you are in the EU/EEA, you have additional rights under GDPR including data portability and the right to lodge a complaint with a supervisory authority.',
      'To exercise any of these rights, contact us at the address below.',
    ],
  },
  {
    title: 'Contact',
    content: [
      'For privacy-related requests, contact: admin@itorzo.com',
      'This privacy policy was last updated: May 2026.',
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950">
      <Navbar />
      <div className="pt-14">
        {/* Header */}
        <div className="border-b-2 border-zinc-950 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
            <Helmet>
              <title>Privacy Policy | CalcByEA™</title>
              <meta name="description" content="CalcByEA privacy policy. We collect your email for login and track purchases. We don't sell data. Stripe handles all payments." />
              <link rel="canonical" href="https://calculatorbyea.com/privacy" />
              <meta property="og:url" content="https://calculatorbyea.com/privacy" />
              <meta property="og:title" content="Privacy Policy | CalcByEA™" />
              <meta property="og:description" content="CalcByEA privacy policy. We collect your email for login and track purchases. We don't sell data. Stripe handles all payments." />
              <meta name="twitter:title" content="Privacy Policy | CalcByEA™" />
              <meta name="twitter:description" content="CalcByEA privacy policy. Email for login, Stripe for payments, no data selling." />
            </Helmet>
            <div className="text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400 mb-4">Legal</div>
            <h1 className="font-bebas text-4xl sm:text-5xl text-zinc-950 mb-3">Privacy Policy</h1>
            <p className="text-zinc-500 text-sm">
              We take your privacy seriously. Unlike EA, we don't have a loot box that costs $4.99 to unlock this page.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="space-y-10">
            {SECTIONS.map(({ title, content }) => (
              <div key={title}>
                <h2 className="font-bebas text-2xl text-zinc-950 mb-3 border-b border-zinc-200 pb-2">{title}</h2>
                <ul className="space-y-2">
                  {content.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-zinc-600 leading-relaxed">
                      <span className="text-zinc-300 font-bold shrink-0 mt-0.5">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t-2 border-zinc-200 flex gap-6 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Link to="/terms" className="hover:text-zinc-950 transition-colors">Terms of Service</Link>
            <Link to="/" className="hover:text-zinc-950 transition-colors">Back to Calculator</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
